import { observable } from "mobx";
import { api } from "../../api";
import { toast } from "react-toastify";
import { decodeUtf8String, downloadBinaryFile } from "../../utils";

export class SpedImportTxt {
  obs = observable({
    importarTxts: false as boolean,
    importarExcel: false as boolean,
    loadingImport: false as boolean,
    loading: false as boolean,
    tasks_sped: [] as any[],
    length_tasks_sped: 0 as number
  })

  constructor() {
  }

  /**
   * Realiza o download de um arquivo de relatório SPED e salva.
   *
   * Envia uma requisição `POST` para o endpoint de download com o identificador
   * do relatório (`id`) e o formato desejado (`format`), configurando a resposta
   * como binária (`blob`) para permitir o download do arquivo.
   *
   * Em caso de sucesso (`status 200`), monta o nome do arquivo no padrão
   * `{cnpj}_relatorio.{format}` e utiliza `downloadBinaryFile` para iniciar
   * o download no navegador.
   *
   * Durante a execução, controla o estado de carregamento por meio de
   * `this.obs.loading`, permitindo indicar processamento na interface.
   * Em caso de erro de resposta ou exceção inesperada, exibe mensagem
   * ao usuário utilizando `toast.error`.
   *
   * @async
   * @function handleDownloadArquivoRelatorio
   * @param {string} id - Identificador do relatório a ser baixado.
   * @param {string} cnpj - CNPJ utilizado para compor o nome final do arquivo.
   * @param {string} format - Formato/extensão do arquivo (ex.: `txt`, `xlsx`).
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é
   *   iniciar o download do arquivo no navegador e atualizar o estado de loading.
  */

  async handleDownloadArquivoRelatorio(id: string, cnpj: string, format: string) {
    
    this.obs.loading = true

    try {

      let resp = await api.post(`/api/relatorio/task/download_file_sped`, {id: id, format: format}, { responseType: 'blob' });

      if(resp.status == 200){

        const fileName = cnpj + "_relatorio." + format;

        downloadBinaryFile(resp.data, fileName)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast.error('erro ao baixar o arquivo, tente novamente mais tarde ou contate o suporte.', {});

    }

    this.obs.loading = false
  }

  /**
   * Realiza a importação de arquivos TXT SPED e exibe feedback ao usuário.
   *
   * Envia uma requisição `POST` para o endpoint de importação de TXT SPED com os dados
   * recebidos em `data`. Durante a execução, controla o estado de carregamento por meio
   * de `this.obs.loadingImport`, permitindo indicar processamento na interface.
   *
   * Após a resposta da API:
   * - Se `resp.data.ok` for `false`, exibe um aviso com a mensagem de erro retornada.
   * - Caso contrário, exibe uma mensagem de sucesso informando que a importação foi concluída.
   *
   * Em caso de execução sem erro, a interface de importação de TXT é fechada/ocultada
   * definindo `this.obs.importarTxts = false`. Em caso de exceção, exibe mensagem de erro
   * genérica para o usuário.
   *
   * @async
   * @function handldeCreateTaskImport
   * @param {any} data - Payload enviado para a importação de TXT SPED. Pode conter
   *   arquivos, metadados e demais parâmetros necessários para o processamento.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é disparar
   *   a importação no backend, atualizar os estados da interface e exibir feedback via toast.
  */

  async handldeCreateTaskImport(data) {

    this.obs.loadingImport = true

    try {
      let resp = await api.post("/api/relatorio/import-txt-sped", data);

      if (resp.data.ok == false) {
        toast.warning(resp.data.erro, {});
      } else {
        toast.success('Importado com sucesso!', {});
      }

      this.obs.importarTxts = false;

    } catch (ex) {
      
      toast.error("Erro ao realizar importação, tente novamente mais tarde.", {});

    }

    this.obs.loadingImport = false

  }

  /**
   * Consulta a lista de tarefas/relatórios SPED Excel paginados por CNPJ e atualiza o estado da tela.
   *
   * Monta um payload com `cnpj`, página atual e limite fixo de 15 registros por página,
   * enviando uma requisição `POST` para o endpoint de consulta de SPED Excel.
   * Após receber a resposta, limpa a lista atual (`this.obs.tasks_sped`) e o total
   * (`this.obs.length_tasks_sped`), preenchendo novamente esses estados apenas se
   * houver itens retornados.
   *
   * Em caso de falha na requisição ou erro inesperado, exibe uma mensagem de erro
   * ao usuário por meio de `toast.error`.
   *
   * @async
   * @function handleConsulta
   * @param {number} paged - Número da página a ser consultada.
   * @param {string} cnpj - CNPJ utilizado como filtro da consulta.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é atualizar
   *   a listagem de tarefas SPED Excel e a quantidade total de registros no estado observável.
  */

  async handleConsulta(paged: number, cnpj: string) {

    try {

      let data = {
        cnpj: cnpj,
        page: paged,
        limit: 15
      }

      let resp = await api.post("/api/relatorio/index-sped-excel", data);

      this.obs.tasks_sped = [];
      this.obs.length_tasks_sped = 0

      if (resp.data.itens.length > 0) {
        this.obs.tasks_sped = resp.data.itens
        this.obs.length_tasks_sped = resp.data.count
      }

    } catch (ex) {

      toast.error("Erro ao tarefas, tente novamente mais tarde.", {});

    }

  }
}

export const SpedImportTxtStore = new SpedImportTxt();
